import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { App } from "./App.js";
import { installChunkReloadHandler } from "./lib/chunk-reload.js";
import { installDebugCapture } from "./lib/debug-log.js";

// Stale lazy chunk po deploy (Vite hash assets) → auto-reload stránky.
// Pokrývá `window error` (script load failures) + `unhandledrejection`
// (React.lazy promise rejections). Anti-loop guard 10s.
installChunkReloadHandler();

// Záchyt chyb z konzole pro admin debug overlay (DebugOverlay). Instaluje se hned
// při startu, ať jsou k dispozici i chyby z načtení stránky. Pro běžné uživatele je
// to levný no-op (zobrazení je gatované rolí admin + debug flagem).
installDebugCapture();

// ── Sentry — init (pouze pokud je DSN nastaveno) ──────────────────────────────
const sentryDsn = import.meta.env["VITE_SENTRY_DSN"] as string | undefined;

if (sentryDsn) {
  const appVersion = import.meta.env["VITE_APP_VERSION"] as string | undefined;
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env["MODE"],
    // exactOptionalPropertyTypes: přidáváme release jen pokud existuje
    ...(appVersion ? { release: appVersion } : {}),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Pouze session replays při chybách
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Vzorkování výkonu (10 % požadavků) + 100 % chybových sessions
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Zaznamenej čas posledního online stavu (pro offline.html)
window.addEventListener("online", () => {
  sessionStorage.setItem("bw:lastOnline", String(Date.now()));
});

// ── React root ────────────────────────────────────────────────────────────────
const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
